/**
 * Small typed fetch wrapper.
 *
 * - Base URL from FRONTEND_API_URL (defaults to `/api/v1`).
 * - Sends credentials (HTTP-only auth cookie).
 * - Throws `ApiError` on non-2xx responses.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly data: unknown;

  get code(): string | undefined {
    if (!this.data || typeof this.data !== 'object' || !('error' in this.data)) return undefined;
    const error = this.data.error;
    return error && typeof error === 'object' && 'code' in error && typeof error.code === 'string'
      ? error.code : undefined;
  }

  constructor(status: number, statusText: string, data: unknown, message?: string) {
    super(message !== undefined ? message : `Request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.data = data;
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body' | 'method'> {
  /** Query params appended to the URL. */
  params?: Record<string, string | number | boolean | null | undefined>;
  /** Override the default Content-Type (application/json). */
  contentType?: string | null;
}

const ENV_BASE = (import.meta.env.FRONTEND_API_URL as string | undefined) ?? '/api/v1';
const BASE_URL = ENV_BASE.replace(/\/$/, '');

function buildUrl(path: string, params?: RequestOptions['params']): string {
  const url = path.startsWith('http')
    ? path
    : `${BASE_URL}/${path.replace(/^\//, '')}`;
  if (!params) return url;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    search.append(key, String(value));
  }
  const q = search.toString();
  return q ? `${url}${url.includes('?') ? '&' : '?'}${q}` : url;
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }
  return response.text();
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const { params, contentType, headers, ...rest } = options;
  const url = buildUrl(path, params);

  const finalHeaders = new Headers(headers);
  let serialisedBody: BodyInit | null = null;
  if (body !== undefined && body !== null) {
    if (
      body instanceof FormData ||
      body instanceof Blob ||
      body instanceof ArrayBuffer ||
      typeof body === 'string'
    ) {
      serialisedBody = body as BodyInit;
    } else {
      serialisedBody = JSON.stringify(body);
      if (!finalHeaders.has('Content-Type') && contentType !== null) {
        finalHeaders.set('Content-Type', contentType ?? 'application/json');
      }
    }
  }
  if (!finalHeaders.has('Accept')) {
    finalHeaders.set('Accept', 'application/json');
  }

  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: finalHeaders,
    body: serialisedBody,
    ...rest,
  };

  const response = await fetch(url, init);

  if (!response.ok) {
    const data = await parseBody(response);
    const message =
      data && typeof data === 'object' && 'message' in data && typeof (data as { message: unknown }).message === 'string'
        ? (data as { message: string }).message
        : undefined;
    throw new ApiError(response.status, response.statusText, data, message);
  }

  return (await parseBody(response)) as T;
}

export const api = {
  get: <T = unknown>(path: string, options?: RequestOptions) =>
    request<T>('GET', path, undefined, options),
  post: <T = unknown>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('POST', path, body, options),
  put: <T = unknown>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PUT', path, body, options),
  patch: <T = unknown>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PATCH', path, body, options),
  delete: <T = unknown>(path: string, options?: RequestOptions) =>
    request<T>('DELETE', path, undefined, options),
};

export type Api = typeof api;
